/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSkillSuggestionsRequest,
  ListSkillSuggestionsSecurity,
} from "../models/operations/listskillsuggestions.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillSuggestionsInfiniteQuery,
  buildSkillSuggestionsQuery,
  prefetchSkillSuggestions,
  prefetchSkillSuggestionsInfinite,
  queryKeySkillSuggestions,
  queryKeySkillSuggestionsInfinite,
  SkillSuggestionsInfiniteQueryData,
  SkillSuggestionsPageParams,
  SkillSuggestionsQueryData,
} from "./skillSuggestions.core.js";
export {
  buildSkillSuggestionsInfiniteQuery,
  buildSkillSuggestionsQuery,
  prefetchSkillSuggestions,
  prefetchSkillSuggestionsInfinite,
  queryKeySkillSuggestions,
  queryKeySkillSuggestionsInfinite,
  type SkillSuggestionsInfiniteQueryData,
  type SkillSuggestionsPageParams,
  type SkillSuggestionsQueryData,
};

export type SkillSuggestionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSuggestions skills
 *
 * @remarks
 * List open skill edit suggestions in the project, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillSuggestions(
  request?: ListSkillSuggestionsRequest | undefined,
  security?: ListSkillSuggestionsSecurity | undefined,
  options?: QueryHookOptions<
    SkillSuggestionsQueryData,
    SkillSuggestionsQueryError
  >,
): UseQueryResult<SkillSuggestionsQueryData, SkillSuggestionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillSuggestionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSuggestions skills
 *
 * @remarks
 * List open skill edit suggestions in the project, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillSuggestionsSuspense(
  request?: ListSkillSuggestionsRequest | undefined,
  security?: ListSkillSuggestionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SkillSuggestionsQueryData,
    SkillSuggestionsQueryError
  >,
): UseSuspenseQueryResult<
  SkillSuggestionsQueryData,
  SkillSuggestionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillSuggestionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSuggestions skills
 *
 * @remarks
 * List open skill edit suggestions in the project, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillSuggestionsInfinite(
  request?: ListSkillSuggestionsRequest | undefined,
  security?: ListSkillSuggestionsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SkillSuggestionsInfiniteQueryData,
    SkillSuggestionsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<SkillSuggestionsInfiniteQueryData, SkillSuggestionsPageParams>,
  SkillSuggestionsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SkillSuggestionsInfiniteQueryData,
    SkillSuggestionsQueryError,
    InfiniteData<SkillSuggestionsInfiniteQueryData, SkillSuggestionsPageParams>,
    QueryKey,
    SkillSuggestionsPageParams
  >({
    ...buildSkillSuggestionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listSuggestions skills
 *
 * @remarks
 * List open skill edit suggestions in the project, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillSuggestionsInfiniteSuspense(
  request?: ListSkillSuggestionsRequest | undefined,
  security?: ListSkillSuggestionsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SkillSuggestionsInfiniteQueryData,
    SkillSuggestionsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<SkillSuggestionsInfiniteQueryData, SkillSuggestionsPageParams>,
  SkillSuggestionsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SkillSuggestionsInfiniteQueryData,
    SkillSuggestionsQueryError,
    InfiniteData<SkillSuggestionsInfiniteQueryData, SkillSuggestionsPageParams>,
    QueryKey,
    SkillSuggestionsPageParams
  >({
    ...buildSkillSuggestionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSkillSuggestionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      skillId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillSuggestionsQueryData,
): SkillSuggestionsQueryData | undefined {
  const key = queryKeySkillSuggestions(...queryKeyBase);

  return client.setQueryData<SkillSuggestionsQueryData>(key, data);
}

export function invalidateSkillSuggestions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      skillId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listSuggestions", ...queryKeyBase],
  });
}

export function invalidateAllSkillSuggestions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listSuggestions"],
  });
}
